<script setup>
// Ellie Desk: suggestion context card pinned at the top of the copilot panel —
// shows the grounding (confidence, sources, agent notes) behind the latest
// Ellie reply suggestion. Core-touch, see custom/README.md.
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  // Normalized shape from useCopilotSuggestionContext:
  // { citations: Array, agentNotes: String, confidence: Number|null }
  context: {
    type: Object,
    required: true,
  },
});

const SOURCE_TYPE_ICONS = {
  'kb-website': 'i-lucide-globe',
  'kb-article': 'i-lucide-book-open',
  conversation: 'i-lucide-messages-square',
};

// One source per page: multiple retrieved chunks of the same URL collapse
// into a single entry (first occurrence wins — citations arrive score-sorted).
const dedupedSources = computed(() => {
  const seen = new Set();
  return (props.context.citations || []).filter(citation => {
    const key = citation.url || citation.title;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
});

const confidenceLabel = computed(() =>
  typeof props.context.confidence === 'number'
    ? props.context.confidence.toFixed(2)
    : null
);

const sourceIcon = citation =>
  SOURCE_TYPE_ICONS[citation.sourceType] || 'i-lucide-file-text';
</script>

<template>
  <div
    class="w-full rounded-lg border border-n-weak bg-n-solid-1 p-3 space-y-3"
  >
    <div class="flex items-center justify-between gap-2">
      <span class="text-xs font-medium text-n-slate-12">
        {{ $t('CAPTAIN.COPILOT.CONTEXT_CARD.TITLE') }}
      </span>
      <span
        v-if="confidenceLabel"
        class="text-xs px-1.5 py-0.5 rounded-md bg-n-slate-3 text-n-slate-11 whitespace-nowrap"
      >
        {{
          $t('CAPTAIN.COPILOT.CONTEXT_CARD.CONFIDENCE', {
            value: confidenceLabel,
          })
        }}
      </span>
    </div>

    <div v-if="dedupedSources.length" class="space-y-1.5">
      <span class="text-xs text-n-slate-10 block">
        {{ $t('CAPTAIN.COPILOT.CONTEXT_CARD.SOURCES') }}
      </span>
      <div
        v-for="source in dedupedSources"
        :key="source.url || source.title"
        class="text-xs leading-5"
      >
        <div class="flex items-center gap-1.5 min-w-0">
          <Icon
            :icon="sourceIcon(source)"
            class="size-3 text-n-slate-10 shrink-0"
          />
          <a
            v-if="source.url"
            :href="source.url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-n-slate-12 hover:underline truncate"
          >
            {{ source.title || source.url }}
          </a>
          <span v-else class="text-n-slate-12 truncate">
            {{ source.title }}
          </span>
        </div>
        <p
          v-if="source.tool"
          class="text-n-slate-10 truncate ltr:pl-[1.125rem] rtl:pr-[1.125rem] mb-0"
        >
          {{
            $t('CAPTAIN.COPILOT.CONTEXT_CARD.VIA', {
              tool: source.tool,
              query: source.query,
            })
          }}
        </p>
      </div>
    </div>

    <div v-if="context.agentNotes" class="space-y-1">
      <span class="text-xs text-n-slate-10 flex items-center gap-1">
        <Icon icon="i-lucide-lock" class="size-3 shrink-0" />
        {{ $t('CAPTAIN.COPILOT.CONTEXT_CARD.AGENT_NOTES') }}
      </span>
      <p class="text-xs text-n-slate-11 leading-5 whitespace-pre-wrap mb-0">
        {{ context.agentNotes }}
      </p>
    </div>
  </div>
</template>
